import React, { useRef, useState, useEffect, useCallback } from 'react';

interface SignaturePadProps {
  /** Current mode — typed or drawn */
  mode: 'typed' | 'drawn';
  /** Callback when mode changes */
  onModeChange: (mode: 'typed' | 'drawn') => void;
  /** Current typed name value */
  typedValue: string;
  /** Callback when typed name changes */
  onTypedChange: (value: string) => void;
  /** Callback when drawn signature data URL changes */
  onDrawnChange: (dataUrl: string) => void;
  /** Previously saved drawn signature data URL (used to restore canvas) */
  drawnDataUrl?: string;
  /** Printed name for drawn mode (separate from the drawn signature image) */
  printedName?: string;
  /** Callback when printed name changes (drawn mode only) */
  onPrintedNameChange?: (name: string) => void;
}

/**
 * Electronic signature component supporting typed name or drawn signature.
 * Extracted from ReviewSign to be reusable.
 */
const SignaturePad: React.FC<SignaturePadProps> = ({
  mode,
  onModeChange,
  typedValue,
  onTypedChange,
  onDrawnChange,
  drawnDataUrl,
  printedName = '',
  onPrintedNameChange,
}) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [isDrawing, setIsDrawing] = useState(false);

  // Initialize canvas with proper sizing and restore previous drawing if available
  useEffect(() => {
    const canvas = canvasRef.current;
    if (canvas && canvas.parentElement) {
      const parent = canvas.parentElement;
      canvas.width = parent.clientWidth;
      canvas.height = parent.clientHeight;
      const ctx = canvas.getContext('2d');
      if (ctx) {
        ctx.fillStyle = 'white';
        ctx.fillRect(0, 0, canvas.width, canvas.height);

        // Restore previously drawn signature
        if (drawnDataUrl) {
          const img = new Image();
          img.onload = () => {
            ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
          };
          img.src = drawnDataUrl;
        }
      }
    }
  }, [mode, drawnDataUrl]);

  const isCanvasDrawn = useCallback((): boolean => {
    if (!canvasRef.current) return false;
    const ctx = canvasRef.current.getContext('2d');
    if (!ctx) return false;
    const imageData = ctx.getImageData(0, 0, canvasRef.current.width, canvasRef.current.height);
    return imageData.data.some((pixel, index) => index % 4 !== 3 && pixel !== 255);
  }, []);

  const getCoords = (e: React.MouseEvent<HTMLCanvasElement> | React.TouchEvent<HTMLCanvasElement>) => {
    const canvas = canvasRef.current;
    if (!canvas) return { x: 0, y: 0 };
    const rect = canvas.getBoundingClientRect();
    if ('touches' in e) {
      return { x: e.touches[0].clientX - rect.left, y: e.touches[0].clientY - rect.top };
    }
    return { x: e.nativeEvent.offsetX, y: e.nativeEvent.offsetY };
  };

  const startDrawing = (e: React.MouseEvent<HTMLCanvasElement> | React.TouchEvent<HTMLCanvasElement>) => {
    e.preventDefault();
    setIsDrawing(true);
    const ctx = canvasRef.current?.getContext('2d');
    const { x, y } = getCoords(e);
    if (ctx) { ctx.beginPath(); ctx.moveTo(x, y); }
  };

  const draw = (e: React.MouseEvent<HTMLCanvasElement> | React.TouchEvent<HTMLCanvasElement>) => {
    if (!isDrawing) return;
    e.preventDefault();
    const ctx = canvasRef.current?.getContext('2d');
    const { x, y } = getCoords(e);
    if (ctx) {
      ctx.lineTo(x, y);
      ctx.strokeStyle = '#0F2647';
      ctx.lineWidth = 2;
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';
      ctx.stroke();
    }
  };

  const stopDrawing = (e?: React.MouseEvent<HTMLCanvasElement> | React.TouchEvent<HTMLCanvasElement>) => {
    if (e) e.preventDefault();
    setIsDrawing(false);
    if (canvasRef.current && isCanvasDrawn()) {
      onDrawnChange(canvasRef.current.toDataURL('image/png'));
    }
  };

  const clearCanvas = () => {
    const canvas = canvasRef.current;
    if (canvas) {
      const ctx = canvas.getContext('2d');
      if (ctx) {
        ctx.fillStyle = 'white';
        ctx.fillRect(0, 0, canvas.width, canvas.height);
      }
    }
    onDrawnChange('');
  };

  /** Expose helper to parent to check if canvas has content */
  const hasSignature = typedValue.trim().length >= 2 || isCanvasDrawn();

  return (
    <div style={{
      background: '#fff',
      border: '1.5px solid #1B3A6B',
      borderRadius: 16,
      padding: '20px 24px',
      marginBottom: 16,
      boxShadow: '0 2px 12px rgba(27,58,107,0.09)',
    }}>
      {/* Title */}
      <div style={{
        display: 'flex', alignItems: 'center',
        marginBottom: 18, paddingBottom: 14,
        borderBottom: '1px solid #F1F5F9',
      }}>
        <h3 style={{
          margin: 0, fontSize: 14, fontWeight: 700,
          color: '#1B3A6B', textTransform: 'uppercase', letterSpacing: '0.06em',
        }}>
          Electronic Signature
        </h3>
      </div>

      {/* Mode Tabs */}
      <div style={{
        display: 'flex', gap: 0,
        marginBottom: 20,
        border: '1px solid #E2E8F0',
        borderRadius: 10,
        overflow: 'hidden',
      }}>
        {([
          { key: 'typed' as const, icon: '✏️', label: 'Type Name' },
          { key: 'drawn' as const, icon: '🖊️', label: 'Draw Signature' },
        ]).map((tab) => (
          <button
            key={tab.key}
            onClick={() => onModeChange(tab.key)}
            data-testid={`signature-mode-${tab.key}`}
            style={{
              flex: 1,
              padding: '12px 16px',
              background: mode === tab.key
                ? 'linear-gradient(135deg, #0F2647 0%, #1B3A6B 100%)'
                : '#fff',
              border: 'none',
              color: mode === tab.key ? '#fff' : '#64748B',
              fontSize: 13,
              fontWeight: 600,
              cursor: 'pointer',
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
              transition: 'all 0.2s ease',
            }}
          >
            <span>{tab.icon}</span> {tab.label}
          </button>
        ))}
      </div>

      {/* Typed Signature */}
      {mode === 'typed' && (
        <div>
          <label style={{
            display: 'block', color: '#1E293B', fontSize: 12,
            fontWeight: 700, marginBottom: 8,
            textTransform: 'uppercase', letterSpacing: '0.06em',
          }}>Authorised Signatory Name *</label>
          <input
            id="signatureData"
            data-testid="signature-typed-input"
            type="text"
            placeholder="Enter your full name"
            value={typedValue}
            onChange={(e) => onTypedChange(e.target.value)}
            maxLength={80}
            style={{
              width: '100%', padding: '13px 16px',
              border: '2px solid #E2E8F0', borderRadius: 10,
              fontSize: 15, fontFamily: 'inherit',
              transition: 'border-color 0.2s',
              boxSizing: 'border-box',
              outline: 'none',
            }}
            onFocus={(e) => { e.currentTarget.style.borderColor = '#0F2647'; }}
            onBlur={(e) => { e.currentTarget.style.borderColor = '#E2E8F0'; }}
          />
          <div style={{ fontSize: 11, color: '#94A3B8', marginTop: 6, display: 'flex', alignItems: 'center', gap: 6 }}>
            💡 Type your full legal name as it appears in official records
          </div>

          {typedValue.trim() && (
            <div style={{
              marginTop: 16, padding: '16px 20px',
              background: '#F8FAFC', borderRadius: 10,
              border: '1px dashed #0F2647',
            }}>
              <div style={{ fontSize: 10, color: '#0F2647', textTransform: 'uppercase', fontWeight: 700, marginBottom: 8, letterSpacing: '0.06em' }}>
                Signature Preview
              </div>
              <div style={{
                fontSize: 30, fontFamily: "'Brush Script MT', 'Segoe Script', cursive",
                color: '#0F2647', letterSpacing: 1.5,
              }}>
                {typedValue}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Drawn Signature */}
      {mode === 'drawn' && (
        <div>
          <label style={{
            display: 'block', color: '#1E293B', fontSize: 12,
            fontWeight: 700, marginBottom: 6,
            textTransform: 'uppercase', letterSpacing: '0.06em',
          }}>Draw Your Signature *</label>
          <div style={{ fontSize: 11, color: '#94A3B8', marginBottom: 10 }}>
            Use your mouse or stylus to sign below
          </div>
          <div style={{
            border: '2px solid #E2E8F0',
            borderRadius: 10,
            overflow: 'hidden',
            width: '100%',
            height: 180,
            marginBottom: 10,
            background: '#fff',
          }}>
            <canvas
              ref={canvasRef}
              data-testid="signature-canvas"
              onMouseDown={startDrawing}
              onMouseMove={draw}
              onMouseUp={stopDrawing}
              onMouseLeave={stopDrawing}
              onTouchStart={startDrawing}
              onTouchMove={draw}
              onTouchEnd={stopDrawing}
              onTouchCancel={stopDrawing}
              style={{
                display: 'block', cursor: 'crosshair',
                width: '100%', height: '100%',
                touchAction: 'none', userSelect: 'none',
                WebkitUserSelect: 'none',
              }}
            />
          </div>
          <button
            data-testid="signature-canvas-clear-btn"
            onClick={clearCanvas}
            style={{
              padding: '8px 16px',
              background: '#FEF2F2', border: '1px solid #FECACA',
              borderRadius: 8, color: '#DC2626',
              fontSize: 12, fontWeight: 600,
              cursor: 'pointer', textTransform: 'uppercase',
            }}
          >
            ✕ Clear Signature
          </button>

          <div style={{ marginTop: 20 }}>
            <label style={{
              display: 'block', color: '#1E293B', fontSize: 12,
              fontWeight: 700, marginBottom: 8,
              textTransform: 'uppercase', letterSpacing: '0.06em',
            }}>Full Name (Printed) *</label>
            <input
              data-testid="signature-printed-name-input"
              type="text"
              placeholder="Enter your full legal name"
              value={printedName}
              onChange={(e) => onPrintedNameChange?.(e.target.value)}
              maxLength={80}
              style={{
                width: '100%', padding: '13px 16px',
                border: '2px solid #E2E8F0', borderRadius: 10,
                fontSize: 15, fontFamily: 'inherit',
                transition: 'border-color 0.2s',
                boxSizing: 'border-box',
                outline: 'none',
              }}
              onFocus={(e) => { e.currentTarget.style.borderColor = '#0F2647'; }}
              onBlur={(e) => { e.currentTarget.style.borderColor = '#E2E8F0'; }}
            />
            <div style={{ fontSize: 11, color: '#94A3B8', marginTop: 6 }}>
              💡 Print your full legal name as it appears in official records
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export { SignaturePad };
export type { SignaturePadProps };
export default SignaturePad;
